/* eslint-disable react/no-array-index-key */
import React, { useState, memo } from 'react';
import { createPortal } from 'react-dom';

import Heading from '@havenengineering/module-shared-library/dist/components/Heading';
import OverlayPanel, {
  DesktopVariants,
} from '@havenengineering/module-shared-library/dist/components/OverlayPanel';
import { getParkByCode } from '@havenengineering/module-shared-library/dist/utils/parks/getters';
import { HolidayType } from '@havenengineering/module-shared-library/dist/utils/parks/types';
import VideoPlayer from '@havenengineering/module-shared-library/dist/components/VideoPlayer';
import Button, {
  Theme,
} from '@havenengineering/module-shared-library/dist/components/Button/Button';
import classNames from 'classnames';
import ContentfulPicture from '../../widget/contentful-picture';
import ContentfulImage from '../../widget/contentful-image';
import { accommodationGalleryProfiles } from '../../../../constant/image-profiles';

import styles from './accommodation-gallery.module.scss';
import { handleAccommodationGalleryOverlay } from '../../../../helpers/gtm/gtm-shared';
import { filterHighlightCarouselItems } from '../../../../helpers/carouselItems';
import { SearchResultImage } from '../../../../types/v3/api-response';
import MatterPortFrame from '../matterport-frame';
import { GalleryItem } from '../../../../types/shared';
import ContentPicture from '../content-picture';
import { Image } from '../../../caravan-details-page/types';
import ImageCaption from './image-caption';
import { analytics } from '../../../../helpers/gtm/analytics';

type Props = {
  activeTab?: string;
  backButtonLabel?: string;
  open?: boolean;
  resultToShowInGalleryGeneral: GalleryItem;
  setGalleryOpen: (value: boolean) => void;
  showLegalText?: boolean;
  galleryType?: 'caravan' | 'accommodation';
  withParkInfo?: boolean;
  isCaravanSearch?: boolean;
  caravanImagesSource?: string;
  showFooter?: boolean;
  doneButtonLabel?: string;
  isSoldOut?: boolean;
  overlayButtonClick?: () => void;
  overlayButtonLabel?: string;
};

export const AccommodationGallery = ({
  activeTab = 'accommodation',
  backButtonLabel = 'Back to search',
  open = false,
  resultToShowInGalleryGeneral,
  setGalleryOpen,
  showLegalText = true,
  galleryType = 'accommodation',
  withParkInfo = true,
  isCaravanSearch,
  caravanImagesSource = '',
  showFooter = false,
  doneButtonLabel,
  isSoldOut,
  overlayButtonClick,
  overlayButtonLabel,
}: Props) => {
  const {
    caravanImages,
    parkFeatures,
    floorPlan,
    matterPortId,
    accomType,
    accomGrade,
    parkName,
    accommodationName,
    parkCode,
    products,
    video,
  } = resultToShowInGalleryGeneral;

  const accommodationGalleryImages =
    caravanImages && !isCaravanSearch
      ? resultToShowInGalleryGeneral?.caravanImages.map((image: any) => ({
          ...image,
          title: image.altText,
          lazy: true,
        }))
      : [];
  const accommodationTotalImages = accommodationGalleryImages.length;
  const highlightCarouselItems = filterHighlightCarouselItems(parkFeatures);
  const [showGalleryTab, setGalleryTab] = useState(activeTab);

  const floorPlanImage =
    floorPlan && floorPlan.length > 0 && (!isCaravanSearch ? JSON.parse(floorPlan) : floorPlan);

  const checkMatterPort = !!matterPortId && matterPortId.length > 0;

  const handleGalleryTab = (tab: string) => {
    const galleryTop = document.getElementById('galleryTop');
    galleryTop?.scrollIntoView();
    setGalleryTab(tab);
    handleAccommodationGalleryOverlay(
      tab,
      accomType?.toLowerCase() || '',
      accomGrade?.toLowerCase() || '',
      !!floorPlanImage,
      checkMatterPort,
      products,
      isCaravanSearch,
      caravanImagesSource,
    );
  };

  const showTabs =
    isCaravanSearch && !checkMatterPort && !floorPlanImage && highlightCarouselItems?.length < 1;

  const overlayButtonEnabled = !!overlayButtonClick && !!overlayButtonLabel;

  const getLegalText = () => {
    if (galleryType === 'caravan')
      return 'Images may show optional extras, such as decking, which are not included in the price but may be available at an additional cost.';
    if (galleryType === 'accommodation')
      return 'Please note: All imagery shown is for representative purposes only. Appearance, size and views will vary by park and location.';
  };

  if (!open) return null;

  return createPortal(
    <>
      <OverlayPanel
        open
        setPanelOpen={setGalleryOpen}
        backButton
        backButtonLabel={backButtonLabel}
        showFooter={showFooter}
        closeButton
        close={() => setGalleryOpen(false)}
        doneButtonLabel={doneButtonLabel}
        desktopVariant={DesktopVariants.FULLSCREEN}
        level={1}
      >
        <>
          <div id="galleryTop" />
          <div
            className={classNames(styles.accommodationGalleryAlternateRoot, {
              [styles.overlayButtonEnabled]: overlayButtonEnabled,
            })}
          >
            {!showTabs && (
              <div className={styles.tabList}>
                <button
                  className={`${styles.tabItem} ${
                    showGalleryTab === 'accommodation' ? styles.tabItemActive : ''
                  }`}
                  type="button"
                  onClick={() => handleGalleryTab('accommodation')}
                  tabIndex={showGalleryTab === 'accommodation' ? -1 : 0}
                >
                  Accommodation
                </button>
                {withParkInfo && (
                  <button
                    className={`${styles.tabItem} ${
                      showGalleryTab === 'park' ? styles.tabItemActive : ''
                    }`}
                    type="button"
                    onClick={() => handleGalleryTab('park')}
                    tabIndex={showGalleryTab === 'park' ? -1 : 0}
                  >
                    Park
                  </button>
                )}
                {(checkMatterPort || floorPlanImage) && (
                  <button
                    className={`${styles.tabItem} ${
                      showGalleryTab === 'matterport' ? styles.tabItemActive : ''
                    }`}
                    type="button"
                    onClick={() => handleGalleryTab('matterport')}
                    tabIndex={showGalleryTab === 'matterport' ? -1 : 0}
                  >
                    {checkMatterPort && floorPlanImage && 'Virtual tour & floor plan'}
                    {checkMatterPort && !floorPlanImage && 'Virtual tour'}
                    {!checkMatterPort && floorPlanImage && 'Floor plan'}
                  </button>
                )}
              </div>
            )}
            {showLegalText && <div className={styles.legalText}>{getLegalText()}</div>}
            {showGalleryTab === 'park' && (
              <div className={styles.accommodationGalleryRoot}>
                <>
                  <Heading scale={3} className={styles.title}>
                    {parkName} ({getParkByCode(HolidayType.HOLIDAY, parkCode)?.region.name})
                  </Heading>
                  <ul className={styles.imageGallery}>
                    {highlightCarouselItems?.map(({ id, title, images: [image] }, idx) => (
                      <li key={id} className={styles.imageItem}>
                        <div className={styles.imageContainer}>
                          <ContentfulPicture
                            className={styles.image}
                            alt={image.altText || ''}
                            url={image.url || ''}
                            profiles={accommodationGalleryProfiles}
                            imageId={image.url || ''}
                          />
                        </div>
                        <ImageCaption
                          index={idx}
                          numberOfImages={highlightCarouselItems.length}
                          text={title}
                          showText={!isCaravanSearch}
                        />
                      </li>
                    ))}
                  </ul>
                </>
              </div>
            )}
            {showGalleryTab === 'accommodation' && (
              <div className={styles.accommodationGalleryRoot}>
                <>
                  <Heading scale={3} className={styles.title}>
                    {accommodationName}
                  </Heading>
                  <ul className={styles.imageGallery}>
                    {!isCaravanSearch && (
                      <>
                        {video?.videoUrl ? (
                          <li key={video.title} className={styles.imageItem}>
                            <VideoPlayer
                              video={video.videoUrl}
                              poster={video.posterImage?.url}
                              title={video.title || undefined}
                              analytics={(dataLayer) => analytics(dataLayer, false)}
                              muted
                            />
                          </li>
                        ) : null}
                        {accommodationGalleryImages.map((image: SearchResultImage, idx: number) => (
                          <li key={image.id} className={styles.imageItem}>
                            <div className={styles.imageContainer}>
                              <ContentfulImage
                                className={styles.image}
                                src={image?.url}
                                profiles={accommodationGalleryProfiles}
                                alt={image?.altText}
                              />
                            </div>
                            <ImageCaption
                              index={idx}
                              numberOfImages={accommodationTotalImages}
                              text={image.altText}
                              showText={!isCaravanSearch}
                            />
                          </li>
                        ))}
                      </>
                    )}
                    {isCaravanSearch &&
                      resultToShowInGalleryGeneral?.caravanImages.map(
                        (image: Image, index: number) => (
                          <li key={image.altText} className={styles.imageItem}>
                            <ContentPicture
                              image={image}
                              className={styles.picture}
                              size="defaultSrc"
                              key={`image${image.altText}_${index}`}
                            />
                            <ImageCaption
                              index={index}
                              numberOfImages={resultToShowInGalleryGeneral?.caravanImages.length}
                              text={image.altText}
                              showText={!isCaravanSearch}
                            />
                          </li>
                        ),
                      )}
                  </ul>
                </>
              </div>
            )}
            {showGalleryTab === 'matterport' &&
              ((!!matterPortId && matterPortId.length > 0) || floorPlanImage) && (
                <div className={styles.accommodationGalleryRoot}>
                  {!!matterPortId && matterPortId.length > 0 && (
                    <MatterPortFrame matterportId={matterPortId} />
                  )}
                  <div className={styles.floorPlanRoot}>
                    <div className={styles.floorPlanImage}>
                      {isCaravanSearch ? (
                        <ContentPicture
                          image={{ altText: 'floorPlan', sizes: { defaultSrc: floorPlanImage } }}
                        />
                      ) : (
                        <ContentfulPicture
                          alt={floorPlanImage?.altText || ''}
                          url={floorPlanImage?.url || ''}
                          profiles={accommodationGalleryProfiles}
                        />
                      )}
                    </div>
                    <p className={styles.floorPlanDescription}>Floor plan</p>
                  </div>
                </div>
              )}
          </div>
          {overlayButtonEnabled && (
            <div className={styles.overlayButtonContainer}>
              <Button
                className={styles.inactiveButton}
                theme={Theme.PRIMARY_THEME}
                block
                onClick={overlayButtonClick}
                disabled={isSoldOut}
                data-testid="overlayButton"
              >
                {overlayButtonLabel}
              </Button>
            </div>
          )}
        </>
      </OverlayPanel>
    </>,
    document.getElementById('portal') as HTMLElement,
  );
};

export default memo(AccommodationGallery);
